import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Table from '../../components/ui/Table';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import {
  Plus,
  Search,
  Eye,
  Edit2,
  Trash2,
  User,
  Users,
  Briefcase,
  UserCheck,
  MapPin,
  Car,
  Camera,
  AlertTriangle,
  Clock,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { personService } from '../../services';
import { useLocation as useLocationContext } from '../../contexts/LocationContext';
import type { Person, PersonType } from '../../types';

type FilterTab = 'ALL' | PersonType;

const PERSON_TYPE_CONFIG: Record<PersonType, { label: string; icon: React.ReactNode; color: string }> = {
  EMPLOYEE: { label: 'Employee', icon: <UserCheck size={14} />, color: 'bg-blue-500/10 text-blue-500' },
  SUBCONTRACTOR: { label: 'Subcontractor', icon: <Briefcase size={14} />, color: 'bg-purple-500/10 text-purple-500' },
  VISITOR: { label: 'Visitor', icon: <Users size={14} />, color: 'bg-green-500/10 text-green-500' },
};

const ACCESS_LEVEL_CONFIG: Record<string, { label: string; color: string }> = {
  FULL: { label: 'Full Access', color: 'bg-green-500/10 text-green-500' },
  LIMITED: { label: 'Limited', color: 'bg-yellow-500/10 text-yellow-500' },
  ESCORT_REQUIRED: { label: 'Escort Required', color: 'bg-orange-500/10 text-orange-500' },
  NONE: { label: 'No Access', color: 'bg-red-500/10 text-red-500' },
};

const PersonsPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedLocationId: globalLocationId } = useLocationContext();
  
  // Data state
  const [persons, setPersons] = useState<Person[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Filter state
  const [activeTab, setActiveTab] = useState<FilterTab>('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const filterLocation = globalLocationId === 'all' ? '' : globalLocationId;
  const [includeInactive, setIncludeInactive] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 20;
  
  // Delete modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [personToDelete, setPersonToDelete] = useState<Person | null>(null);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [globalLocationId]);

  const personRequestRef = useRef(0);

  // Fetch persons
  const fetchPersons = useCallback(async () => {
    const request = ++personRequestRef.current;
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await personService.getPersons({
        person_type: activeTab !== 'ALL' ? activeTab : undefined,
        location_id: filterLocation ? parseInt(filterLocation) : undefined,
        search: debouncedSearch || undefined,
        include_inactive: includeInactive,
        page: currentPage,
        page_size: pageSize,
      });
      
      if (request !== personRequestRef.current) return;
      setPersons(response.items || []);
      setTotalPages(response.total_pages || 1);
      setTotalItems(response.total || 0);
    } catch (err) {
      if (request === personRequestRef.current) setError(err instanceof Error ? err.message : 'Failed to fetch persons');
    } finally {
      if (request === personRequestRef.current) setIsLoading(false);
    }
  }, [activeTab, filterLocation, debouncedSearch, includeInactive, currentPage]);

  useEffect(() => {
    void fetchPersons();
    return () => { personRequestRef.current += 1; };
  }, [fetchPersons]);

  // Clear success message after timeout
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleDelete = async () => {
    if (!personToDelete) return;
    try {
      await personService.deletePerson(personToDelete.id);
      setSuccessMessage(`${personToDelete.first_name} ${personToDelete.last_name} deleted successfully`);
      setIsDeleteModalOpen(false);
      setPersonToDelete(null);
      fetchPersons();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete person');
    }
  };

  const isExpired = (validUntil?: string) => {
    if (!validUntil) return false;
    return new Date(validUntil) < new Date();
  };

  const isExpiringSoon = (validUntil?: string) => {
    if (!validUntil) return false;
    const expiry = new Date(validUntil);
    const now = new Date();
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    return expiry > now && expiry.getTime() - now.getTime() < sevenDays;
  };

  const tabs: { key: FilterTab; label: string; icon: React.ReactNode }[] = [
    { key: 'ALL', label: 'All', icon: <Users size={16} /> },
    { key: 'EMPLOYEE', label: 'Employees', icon: <UserCheck size={16} /> },
    { key: 'SUBCONTRACTOR', label: 'Subcontractors', icon: <Briefcase size={16} /> },
    { key: 'VISITOR', label: 'Visitors', icon: <User size={16} /> },
  ];

  const columns = [
    {
      key: 'photo',
      label: '',
      render: (person: Person) => (
        <div className="w-10 h-10 rounded-full bg-surface-secondary overflow-hidden flex items-center justify-center">
          {person.primary_photo_url ? (
            <img
              src={person.primary_photo_url}
              alt={`${person.first_name} ${person.last_name}`}
              className="w-full h-full object-cover"
            />
          ) : (
            <User size={20} className="text-text-muted" />
          )}
        </div>
      ),
    },
    {
      key: 'name',
      label: 'Name',
      render: (person: Person) => (
        <div>
          <div className="font-medium text-text-primary flex items-center gap-2">
            {person.first_name} {person.last_name}
            {!person.is_active && (
              <Badge variant="danger" size="sm">Inactive</Badge>
            )}
            {isExpired(person.valid_until) && (
              <Badge variant="danger" size="sm" className="flex items-center gap-1">
                <AlertTriangle size={10} /> Expired
              </Badge>
            )}
            {isExpiringSoon(person.valid_until) && !isExpired(person.valid_until) && (
              <Badge variant="warning" size="sm" className="flex items-center gap-1">
                <Clock size={10} /> Expiring Soon
              </Badge>
            )}
          </div>
          <div className="text-xs text-text-muted">
            {person.employee_id && <span>ID: {person.employee_id} • </span>}
            {person.email || person.phone || 'No contact info'}
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      render: (person: Person) => {
        const config = PERSON_TYPE_CONFIG[person.person_type];
        return (
          <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
            {config.icon}
            {config.label}
          </div>
        );
      },
    },
    {
      key: 'company',
      label: 'Company',
      render: (person: Person) => (
        <div className="text-sm text-text-secondary">
          {person.external_company || '-'}
        </div>
      ),
    },
    {
      key: 'access',
      label: 'Access Level',
      render: (person: Person) => {
        const config = ACCESS_LEVEL_CONFIG[person.access_level] || ACCESS_LEVEL_CONFIG.NONE;
        return (
          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
            {config.label}
          </div>
        );
      },
    },
    {
      key: 'counts',
      label: 'Data',
      render: (person: Person) => (
        <div className="flex items-center gap-3 text-xs text-text-muted">
          <span className="flex items-center gap-1" title="Face photos">
            <Camera size={12} /> {person.face_images_count || 0}
          </span>
          <span className="flex items-center gap-1" title="Locations">
            <MapPin size={12} /> {person.locations_count || 0}
          </span>
          <span className="flex items-center gap-1" title="Vehicles">
            <Car size={12} /> {person.vehicles_count || 0}
          </span>
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right' as const,
      render: (person: Person) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/persons/${person.id}/view`);
            }}
            leftIcon={<Eye size={14} />}
          >
            View
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/persons/${person.id}/edit`);
            }}
            leftIcon={<Edit2 size={14} />}
          >
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setPersonToDelete(person);
              setIsDeleteModalOpen(true);
            }}
            leftIcon={<Trash2 size={14} />}
            className="text-red-500 hover:text-red-600"
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="page-stack">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">Person Registry</h1>
            <p className="page-description">
              Manage employees, subcontractors, and visitors
            </p>
          </div>
          <Button onClick={() => navigate('/persons/create')} leftIcon={<Plus size={18} />}>
            Add Person
          </Button>
        </div>

        {/* Alerts */}
        {error && <Alert variant="error" message={error} onClose={() => setError(null)} />}
        {successMessage && <Alert variant="success" message={successMessage} onClose={() => setSuccessMessage(null)} />}

        {/* Type Filter Tabs */}
        <div className="border-b border-surface-secondary">
          <nav aria-label="Person types" className="-mb-px flex gap-4 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => { setActiveTab(tab.key); setCurrentPage(1); }}
                className={`shrink-0 py-2.5 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                  activeTab === tab.key
                    ? 'border-accent-primary text-accent-primary'
                    : 'border-transparent text-text-muted hover:text-text-primary hover:border-surface-hover'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Filters */}
        <Card padding="md">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by name, badge ID, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftIcon={<Search size={18} />}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="includeInactive"
                checked={includeInactive}
                onChange={(e) => { setIncludeInactive(e.target.checked); setCurrentPage(1); }}
                className="rounded border-surface-secondary"
              />
              <label htmlFor="includeInactive" className="text-sm text-text-secondary">
                Show inactive
              </label>
            </div>
          </div>
        </Card>

        {/* Results Count */}
        <div className="text-sm text-text-muted">
          {totalItems} {totalItems === 1 ? 'person' : 'persons'} found
        </div>

        {/* Table */}
        <Card padding="none">
          {isLoading ? (
            <div className="p-8">
              <Loading size="lg" text="Loading persons..." />
            </div>
          ) : persons.length === 0 ? (
            <div className="text-center py-12 px-4">
              <div className="w-16 h-16 bg-surface-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                <Users size={32} className="text-text-muted" />
              </div>
              <h3 className="text-lg font-medium text-text-primary mb-2">No persons found</h3>
              <p className="text-text-muted mb-4">
                {debouncedSearch || activeTab !== 'ALL' || filterLocation
                  ? 'Try adjusting your filters'
                  : 'Get started by adding your first person'}
              </p>
              {!debouncedSearch && activeTab === 'ALL' && !filterLocation && (
                <Button onClick={() => navigate('/persons/create')} leftIcon={<Plus size={18} />}>
                  Add Person
                </Button>
              )}
            </div>
          ) : (
            <Table
              columns={columns}
              data={persons}
              onRowClick={(person) => navigate(`/persons/${person.id}/view`)}
            />
          )}
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-text-muted">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                leftIcon={<ChevronLeft size={16} />}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                rightIcon={<ChevronRight size={16} />}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => { setIsDeleteModalOpen(false); setPersonToDelete(null); }}
          title="Delete Person"
        >
          <div className="space-y-4">
            <p className="text-text-secondary">
              Are you sure you want to delete <strong>{personToDelete?.first_name} {personToDelete?.last_name}</strong>?
              This will also remove all their face images, location access, and vehicle records.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDelete}>
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default PersonsPage;
